// src/resources/reports/ReportAppPenetration/TrendChart.tsx
//
// "Evolução diária": barras empilhadas No app/Fora + linha de % no app, com
// seletor Valor/Litros/Abastecimentos — mesmo padrão de rádio usado no
// gráfico de ReportFillinByStation.jsx.
import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import { ChartMetric, SeriesPoint, fmtBrl, fmtInt, fmtLiters, fmtPct } from './types';

const BLUE = '#3b82f6';
const GRAY = '#cbd5e1';
const INK = '#0f172a';

type Accessor = { app: keyof SeriesPoint; off: keyof SeriesPoint; format: (v: number) => string };

const METRIC_ACCESSORS: Record<ChartMetric, Accessor> = {
  [ChartMetric.Value]: { app: 'appValue', off: 'offValue', format: fmtBrl },
  [ChartMetric.Volume]: { app: 'appVolume', off: 'offVolume', format: fmtLiters },
  [ChartMetric.Fillins]: { app: 'appFillins', off: 'offFillins', format: fmtInt },
};

const METRIC_LABEL: Record<ChartMetric, string> = {
  [ChartMetric.Value]: 'Valor',
  [ChartMetric.Volume]: 'Litros',
  [ChartMetric.Fillins]: 'Abastecimentos',
};

const CHART_METRICS: ChartMetric[] = [ChartMetric.Value, ChartMetric.Volume, ChartMetric.Fillins];

interface ChartPoint { date: string; app: number; off: number; pct: number | null }

/** 'YYYY-MM-DD' → 'dd/mm'. Sem Date no meio: string de data pura vira UTC e volta um dia. */
const shortDate = (iso: string): string => {
  const [, month, day] = iso.split('-');
  return month && day ? `${day}/${month}` : iso;
};

const TrendChart = ({ series }: { series: SeriesPoint[] }) => {
  const [metric, setMetric] = useState<ChartMetric>(ChartMetric.Value);
  const { app: appKey, off: offKey, format } = METRIC_ACCESSORS[metric];

  const chartData: ChartPoint[] = useMemo(() => series.map(point => {
    const appVal = Number(point[appKey]) || 0;
    const offVal = Number(point[offKey]) || 0;
    const total = appVal + offVal;
    return { date: point.date, app: appVal, off: offVal, pct: total > 0 ? (appVal / total) * 100 : null };
  }), [series, appKey, offKey]);

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Evolução diária</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>No app × Fora do app</Typography>
        </Box>
        {/* Seletor segmentado (pílula) em vez de rádio: o rádio destoava do resto da tela,
            que é toda feita de cards, chips e barras. Mesma linguagem dos presets do Painel 360. */}
        <Box sx={{ display: 'inline-flex', gap: 0.4, p: 0.4, bgcolor: '#f1f5f9', borderRadius: 999 }}>
          {CHART_METRICS.map(m => {
            const active = m === metric;
            return (
              <ButtonBase
                key={m}
                onClick={() => setMetric(m)}
                sx={{
                  px: 1.75, py: 0.7, borderRadius: 999, fontSize: 12.5, fontWeight: 700,
                  color: active ? '#fff' : '#64748b',
                  background: active ? `linear-gradient(135deg, ${BLUE}, #06b6d4)` : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(59,130,246,0.35)' : 'none',
                  transition: 'background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease',
                  '&:hover': active ? {} : { color: '#0f172a', bgcolor: 'rgba(148,163,184,0.18)' },
                }}
              >
                {METRIC_LABEL[m]}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {!chartData.length ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Sem movimento diário no período.</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
              {/* O eixo vem 'YYYY-MM-DD' do backend; quem lê espera dd/mm. Split em vez de
                  new Date(...) pra não cair no fuso (string ISO de data vira UTC e volta um dia). */}
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} minTickGap={16} tickFormatter={shortDate} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={72} tickFormatter={(v: number) => format(v)} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={46} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
              <ReTooltip
                labelFormatter={(label: any) => shortDate(String(label))}
                formatter={(value: any, name: any) => (name === '% no app' ? [fmtPct((Number(value) || 0) / 100, 1), name] : [format(Number(value)), name])}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
              <Bar yAxisId="left" dataKey="app" stackId="fillins" name="No app" fill={BLUE} radius={[0, 0, 0, 0]} maxBarSize={28} />
              <Bar yAxisId="left" dataKey="off" stackId="fillins" name="Fora do app" fill={GRAY} radius={[2, 2, 0, 0]} maxBarSize={28} />
              <Line yAxisId="right" dataKey="pct" name="% no app" stroke={INK} strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default TrendChart;
